import { useEffect, useRef, useCallback, useState } from 'react';
import { toast } from 'sonner';
import { Bell, MessageSquare, CheckCircle } from 'lucide-react';
import tabNotificationManager from '../utils/tabNotifications';

const WS_RECONNECT_DELAY = 3000;
const WS_PING_INTERVAL = 30000;

/**
 * Hook to manage WebSocket connection for real-time notifications
 * @param {string} email - User's email to subscribe to notifications
 * @param {function} onNotification - Callback when notification received
 */
export const useNotifications = (email, onNotification = null) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  const connect = useCallback(() => {
    if (!email || !email.includes('@')) return;

    // Get WebSocket URL from environment
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/notifications/${encodeURIComponent(email)}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected for notifications');
        setIsConnected(true);

        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, WS_PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        try {
          // Handle pong response
          if (event.data === 'pong') return;

          const data = JSON.parse(event.data);
          handleNotification(data, onNotification);
        } catch (e) {
          console.error('Error parsing notification:', e);
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        cleanup();

        // Attempt to reconnect unless intentionally closed
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(connect, WS_RECONNECT_DELAY);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, [email, onNotification]);

  const cleanup = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    cleanup();
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, [cleanup]);

  useEffect(() => {
    if (email) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [email, connect, disconnect]);

  return { isConnected, disconnect, reconnect: connect };
};

/**
 * Handle incoming notification and show toast
 */
const handleNotification = (data, callback) => {
  switch (data.type) {
    case 'connected':
      // Silent connection confirmation
      console.log('Notification service connected');
      break;

    case 'ticket_response':
      // Add tab notification with flashing
      tabNotificationManager.addNotification(`Réponse: ${data.ticket_subject}`);
      
      toast.success(
        <div className="flex items-start gap-3">
          <MessageSquare className="h-5 w-5 text-blue-500 mt-0.5" />
          <div>
            <p className="font-semibold">Nouvelle réponse!</p>
            <p className="text-sm text-gray-600">{data.message}</p>
            <p className="text-xs text-gray-400 mt-1">Ticket: {data.ticket_subject}</p>
          </div>
        </div>,
        {
          duration: 8000,
          action: {
            label: 'Voir',
            onClick: () => {
              // Could navigate to ticket detail
              if (callback) callback(data);
            }
          }
        }
      );
      break;

    case 'ticket_status_change':
      // Add tab notification with flashing
      tabNotificationManager.addNotification(`Statut: ${data.status_label}`);
      
      const statusIcon = data.new_status === 'regle' 
        ? <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
        : <Bell className="h-5 w-5 text-amber-500 mt-0.5" />;
      
      toast.info(
        <div className="flex items-start gap-3">
          {statusIcon}
          <div>
            <p className="font-semibold">Statut mis à jour</p>
            <p className="text-sm text-gray-600">{data.status_label}</p>
            <p className="text-xs text-gray-400 mt-1">Ticket: {data.ticket_subject}</p>
          </div>
        </div>,
        { duration: 6000 }
      );
      break;

    case 'customer_reply':
      // Admin notification - Add tab notification with flashing
      tabNotificationManager.addNotification(`Client: ${data.customer_name}`);
      
      toast.info(
        <div className="flex items-start gap-3">
          <MessageSquare className="h-5 w-5 text-brand-500 mt-0.5" />
          <div>
            <p className="font-semibold">Réponse client</p>
            <p className="text-sm text-gray-600">{data.customer_name} a répondu</p>
            <p className="text-xs text-gray-400 mt-1">Ticket: {data.ticket_subject}</p>
          </div>
        </div>,
        { duration: 8000 }
      );
      break;

    case 'new_live_chat':
      // Urgent live chat notification for admins
      tabNotificationManager.addNotification(`🔴 CHAT EN DIRECT: ${data.customer_name}`);
      
      toast.error(
        <div className="flex items-start gap-3">
          <Bell className="h-5 w-5 text-red-500 mt-0.5 animate-bounce" />
          <div>
            <p className="font-semibold text-red-600">🔴 CHAT EN DIRECT!</p>
            <p className="text-sm text-gray-600">{data.customer_name} attend une réponse</p>
            <p className="text-xs text-gray-400 mt-1">{data.support_type}</p>
          </div>
        </div>,
        { duration: 30000 }
      );
      break;

    default:
      console.log('Unknown notification type:', data.type);
  }

  // Call custom callback if provided
  if (callback && data.type !== 'connected') {
    callback(data);
  }
};

export default useNotifications;
